import { marked } from 'marked';
import { useMemo } from 'react';
import { type GestureResponderEvent } from 'react-native';

import { useFrameWidth } from '@/components/ui/pageFrame';
import RenderHTML, {
  type MixedStyleDeclaration,
  type MixedStyleRecord,
} from 'react-native-render-html';
import { getTokenValue } from 'tamagui';

import { fontSize, space } from '@/constants/tokens';
import { openURL } from '@/src/utils/linkingUtils';

/**
 * PSRichText — renders a stored rich-text MARKDOWN string as tokened native
 * text (atom).
 *
 * Event copy (description, "what's included", terms & conditions) is authored
 * upstream and stored as markdown. We parse it to HTML with `marked` and hand
 * that to `react-native-render-html`, which is the part that turns it into our
 * type scale. This atom is the one place that knows the storage format — the
 * blocks that use it just supply a heading and hand the raw `markdown` here.
 *
 * The rendered set is small and closed: paragraphs, bold/italic (and their
 * nesting), ul/ol lists, headings and links. Anything else markdown allows
 * (tables, code fences, blockquotes) falls through to the library's own
 * defaults un-tokened — if authored copy starts using one, style it below
 * rather than letting it render off-scale.
 *
 * ── The token trap (why every style below is a bridged raw value) ──
 * `react-native-render-html` renders plain RN `<Text>`/`<View>`, NOT Tamagui
 * components. A `$token` string handed to its `baseStyle`/`tagsStyles` would
 * reach the native layer verbatim and render wrong — the same silent trap PSIcon
 * bridges for icon color. So this whole file works in RAW values: colors via
 * `getTokenValue`, sizes/spacing pulled straight off the `fontSize`/`space`
 * token OBJECTS (not the `$` syntax). It's the single seam where that's correct;
 * everything is still sourced from `constants/tokens.ts`, never a bare literal.
 *
 * ── Fonts: bold/italic map to loaded FACES, not weights ──
 * We ship two real GeneralSans faces (Medium, Semibold) and no italic file.
 * Tamagui's weight→face map (tamagui.config.ts) only helps Tamagui's own
 * engine; raw RN `<Text>` won't resolve `fontWeight: '600'` to the Semibold
 * file. So `<strong>`/`<b>` set `fontFamily: 'GeneralSans-Semibold'` directly.
 * `<em>`/`<i>` use `fontStyle: 'italic'` — with no italic face that's a
 * synthetic slant on native; acceptable for the little emphasis our copy uses,
 * and the honest thing until an italic face is added.
 *
 * `contentWidth` is required by the library (it lays out block elements against
 * it). We read the window width; every call site renders full-bleed within the
 * screen's horizontal padding, so the window width is the right basis and there
 * is no per-instance width to thread through.
 *
 * ── `surface`: why the text colour is a prop and not inherited ──
 * Raw RN text can't inherit a colour from a Tamagui parent, and this atom sets
 * its own, so the caller has to say which ground the copy is standing on. The
 * event screens are all `$background`, hence the `light` default; the Access
 * Pass landing page is `$brandDark`, where the default would render near-black
 * on near-black. It names the SURFACE rather than the text colour so a call site
 * can't pair a light ground with light type. This is the only axis: per-node
 * colour still isn't controllable, and dimming is still `opacity` on a wrapper
 * (see TicketTier).
 */
type PSRichTextProps = {
  /** A stored markdown string (paragraphs, bold/italic, lists, headings, links). */
  markdown: string;
  /** The ground the copy sits on; picks the text colour. */
  surface?: 'light' | 'dark';
};

// Family strings must be the exact loaded face names (see src/lib/fonts.ts).
const BODY_FACE = 'GeneralSans-Medium';
const BOLD_FACE = 'GeneralSans-Semibold';

// `breaks: true` makes a single newline a <br> instead of collapsing into the
// preceding line. Authored copy separates lines inconsistently (only a blank
// line is a paragraph break in strict markdown), so this is the forgiving read:
// copy that already double-spaces is unaffected, copy that single-spaces keeps
// its shape instead of running together.
//
// `mangle`/`headerIds` are legacy extras marked 5 warns about unless you opt
// out; neither applies here (email obfuscation, and heading `id` attributes
// that RenderHTML has nothing to do with).
//
// marked is held at 5.x ON PURPOSE — not staleness. `marked-terminal`, which
// @tamagui/cli depends on, peer-requires marked <=5, and it resolves to
// whatever sits hoisted at the tree root. A newer major here invalidates that
// peer, and npm won't nest around it (an `overrides` entry doesn't take: it
// dedupes onto @tamagui/cli's own nested copy instead). We use a frozen sliver
// of the API, so the ceiling costs us nothing.
const MARKED_OPTIONS = {
  async: false,
  gfm: true,
  breaks: true,
  mangle: false,
  headerIds: false,
} as const;

// Per-tag overrides. `<p>`/`<li>` carry the inter-block rhythm; strong/em carry
// emphasis; the list containers get an indent + our spacing between items.
//
// COLOUR-FREE by construction — anything needing `getTokenValue` has to be built
// in render (see `tagsStyles` below), so this object stays safe at module scope.
//
// The `marginTop: 0` resets are load-bearing, not stray literals: the library's
// UA stylesheet gives <p>/<ul>/<ol> a default `marginTop: '1em'`, which would
// stack on top of our `marginBottom` and double every inter-block gap. We drive
// the rhythm from the bottom only, so the top must be zeroed. `0` is the absence
// of a design value (no such thing as a "$zero" spacing token), so the
// no-hardcoded-tokens rule is a false positive here — suppressed per house rule
// (don't restructure correct code to satisfy the linter).
/* eslint-disable local-rules/no-hardcoded-tokens -- marginTop:0 cancels the library's 1em UA default; 0 is "no margin", not a token value. */
const STATIC_TAGS_STYLES: MixedStyleRecord = {
  p: { marginTop: 0, marginBottom: space.md },
  strong: { fontFamily: BOLD_FACE },
  b: { fontFamily: BOLD_FACE },
  em: { fontStyle: 'italic' },
  i: { fontStyle: 'italic' },
  ul: { marginTop: 0 },
  ol: { marginTop: 0 },
  li: { marginBottom: space.xs },
  // Markdown makes `#` headings cheap to author, so they get our scale rather
  // than the library's em-based UA sizes. h3 down all share the body size —
  // deeper levels don't appear in this copy and shouldn't shrink below it.
  h1: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.large,
    marginTop: 0,
    marginBottom: space.sm,
  },
  h2: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.md,
    marginTop: 0,
    marginBottom: space.sm,
  },
  h3: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.base,
    marginTop: 0,
    marginBottom: space.sm,
  },
  h4: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.base,
    marginTop: 0,
    marginBottom: space.sm,
  },
  h5: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.base,
    marginTop: 0,
    marginBottom: space.sm,
  },
  h6: {
    fontFamily: BOLD_FACE,
    fontSize: fontSize.base,
    marginTop: 0,
    marginBottom: space.sm,
  },
};
/* eslint-enable local-rules/no-hardcoded-tokens */

// The library warns when a font family it sees isn't declared as "system".
// Register our two faces so bold text doesn't log a spurious font warning.
const systemFonts = [BODY_FACE, BOLD_FACE];

// The library's own <a> handler defaults to calling Linking.openURL directly,
// which throws on a URL the OS can't open — the exact reason the house rule
// routes links through our wrapper. `href` arrives already normalized.
const renderersProps = {
  a: {
    onPress: (_event: GestureResponderEvent, href: string) => {
      openURL(href);
    },
  },
};

export default function PSRichText({
  markdown,
  surface = 'light',
}: PSRichTextProps) {
  const width = useFrameWidth();

  // baseStyle is the paragraph/body look; every text node inherits from it.
  //
  // Built HERE, in render — never at module scope. `getTokenValue` reads the
  // global Tamagui config, which doesn't exist until `createTamagui()` has run
  // (tamagui.config.ts, imported by app/_layout.tsx). At module scope it runs at
  // IMPORT time, and the web static-render pass pulls in route modules before
  // that layout import — so it reads a null config and crashes the web build.
  // Memoized for the same reason as `source` below (stable identity for
  // RenderHTML), not for the cost of the lookup.
  const baseStyle: MixedStyleDeclaration = useMemo(
    () => ({
      color: getTokenValue(
        surface === 'dark' ? '$textWhite' : '$textDark',
        'color'
      ),
      fontFamily: BODY_FACE,
      fontSize: fontSize.base,
      // Matches createFont's body lineHeight for $base (tamagui.config.ts).
      lineHeight: 20,
    }),
    [surface]
  );

  // Link colour needs `getTokenValue`, so the <a> entry can't live in the
  // module-scope object above — same import-time config trap as `baseStyle`.
  const tagsStyles: MixedStyleRecord = useMemo(() => {
    const linkColor = getTokenValue('$brand', 'color');
    return {
      ...STATIC_TAGS_STYLES,
      a: {
        color: linkColor,
        textDecorationLine: 'underline',
        // The library's own UA style for <a> sets its own blue
        // (`textDecorationColor: '#245dc1'`) independently of `color` — without
        // this, the underline stays that blue even once the text itself is brand.
        textDecorationColor: linkColor,
      },
    };
  }, []);

  // marked returns `string` (not a promise) on the `async: false` overload.
  // Memoized so RenderHTML doesn't reparse — and rebuild its transient render
  // tree — when only unrelated parent state changes.
  //
  // The falsy guard is not defensive noise: `marked.parse` THROWS on null or
  // undefined, and the wire can omit an optional body (T&C especially) even
  // though the type says `string`. Without it, a missing body takes down the
  // whole screen instead of leaving one block empty.
  const source = useMemo(
    () => ({ html: markdown ? marked.parse(markdown, MARKED_OPTIONS) : '' }),
    [markdown]
  );

  return (
    <RenderHTML
      contentWidth={width}
      source={source}
      baseStyle={baseStyle}
      tagsStyles={tagsStyles}
      systemFonts={systemFonts}
      renderersProps={renderersProps}
    />
  );
}
